/**
 * P12.4 — Planning Override Archive (append-only)
 *
 * In-memory append-only sink. Mirrors the pattern of
 * `controlTowerOverrides` and `contractorTelemetry`.
 *
 * Hard rules:
 *   • Append-only. Past entries are never mutated.
 *   • No contractValue. No internal margin. No PII beyond what already
 *     exists on Job (customerName/address are NOT recorded here — only IDs
 *     and structural snapshots).
 *   • Never throws — telemetry must NOT block UI.
 */

import type {
  PlanningOverrideArchiveEntry,
  PlanningOverrideReasonCategory,
} from "./types";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

const MAX = 2000;
const log: PlanningOverrideArchiveEntry[] = [];

function genId(): string {
  return `pov_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function appendPlanningOverride(
  rec: Omit<PlanningOverrideArchiveEntry, "id" | "createdAt"> & {
    id?: string;
    createdAt?: string;
  },
): PlanningOverrideArchiveEntry | null {
  try {
    if (!rec.jobId || !rec.createdBy) return null;
    const entry: PlanningOverrideArchiveEntry = {
      ...rec,
      id: rec.id ?? genId(),
      createdAt: rec.createdAt ?? new Date().toISOString(),
    };
    log.push(entry);
    if (log.length > MAX) log.shift();
    void persistArchiveEntry(entry);
    return entry;
  } catch {
    return null;
  }
}

async function persistArchiveEntry(entry: PlanningOverrideArchiveEntry): Promise<void> {
  try {
    const organizationId = await getCurrentOrganizationId().catch(() => null);
    if (!organizationId) return;
    const { error } = await supabase.from("planning_override_archive").insert({
      id: entry.id,
      organization_id: organizationId,
      job_id: entry.jobId,
      created_by: entry.createdBy,
      created_at: entry.createdAt,
      reason_category: entry.reasonCategory,
      reason_note: entry.reasonNote,
      system_estimate: entry.systemEstimate,
      override_payload: entry.override,
      final_approved: entry.finalApproved,
      inputs_snapshot: entry.inputsSnapshot,
      job_spec_snapshot: entry.jobSpecSnapshot,
    });
    if (error) {
      console.warn("[planningReview.archive] DB persist failed:", error.message);
    }
  } catch (error) {
    console.warn("[planningReview.archive] DB persist threw:", error);
  }
}

export function getPlanningOverrides(): ReadonlyArray<PlanningOverrideArchiveEntry> {
  return log;
}

export function getOverridesForJob(
  jobId: string,
): ReadonlyArray<PlanningOverrideArchiveEntry> {
  return log.filter((e) => e.jobId === jobId);
}

export function getLatestOverrideForJob(
  jobId: string,
): PlanningOverrideArchiveEntry | undefined {
  for (let i = log.length - 1; i >= 0; i--) {
    if (log[i].jobId === jobId) return log[i];
  }
  return undefined;
}

export function clearPlanningOverrides(): void {
  log.length = 0;
}

/* ── Insights helper (read-only) ───────────────────────────────────── */

export interface PlanningOverrideInsights {
  count: number;
  averageDaysDelta: number;
  reasonCategoryCounts: Record<PlanningOverrideReasonCategory, number>;
  jobsSystemUnderEstimated: number;
  jobsCompensationAdjusted: number;
}

export function getPlanningOverrideInsights(): PlanningOverrideInsights {
  const reasonCategoryCounts = {
    missed_condition_at_consultation: 0,
    material_constraint: 0,
    access_logistics_issue: 0,
    customer_specific_requirement: 0,
    sales_input_error: 0,
    pm_judgment_field_experience: 0,
    other: 0,
  } as Record<PlanningOverrideReasonCategory, number>;

  let totalDaysDelta = 0;
  let underEstimated = 0;
  let compAdjusted = 0;

  for (const e of log) {
    reasonCategoryCounts[e.reasonCategory] += 1;
    const delta = e.finalApproved.days - e.systemEstimate.days;
    totalDaysDelta += delta;
    if (delta > 0) underEstimated += 1;
    if (e.finalApproved.payoutAmount !== e.systemEstimate.payoutAmount) {
      compAdjusted += 1;
    }
  }

  return {
    count: log.length,
    averageDaysDelta: log.length === 0 ? 0 : totalDaysDelta / log.length,
    reasonCategoryCounts,
    jobsSystemUnderEstimated: underEstimated,
    jobsCompensationAdjusted: compAdjusted,
  };
}
